<template>
  <div class="profile-field-section is-open">
    <div v-if="selectingOnPhone" class="profile-field-section__header is-selecting">
      <FormCheckbox
        :indeterminate="partiallySelected"
        :afterIndeterminate="false"
        :onChange="onSelectAllClick"
        :value="allSelected"
        :aria-label="$t('table.phone-list.select-all')"
      />
      <span class="profile-field-section__selected">
        {{ $tc('table.phone-list.selected', selected.length, { count: selected.length }) }}
      </span>
      <BaseButton v-if="hasSelection" is-small is-danger has-icon-left @click.native="onDeleteSelected">
        <BaseIcon name="trash-2" />
        {{ $t('actions.delete') }}
      </BaseButton>
      <BaseRoundButton
        :size="44"
        icon="x"
        is-transparent
        :aria-label="$t('actions.cancel')"
        aria-pressed="true"
        @click.native="stopSelecting"
      />
    </div>
    <div v-else class="profile-field-section__header">
      <h2 class="profile-field-section__name">{{ category.name }}</h2>
      <span v-if="isPhone" class="profile-field-section__count number-monospace">{{ entries.length }}</span>

      <div class="profile-field-section__spacer" />

      <!-- Phones: the title line is the category's count line, so Sélectionner sits on it, as above
           the other lists (BasePhoneListControls); it turns into the ✕ that ends the selection. -->
      <BaseRoundButton
        v-if="isPhone && entries.length"
        :size="44"
        icon="check-square"
        is-transparent
        :aria-label="$t('table.phone-list.select')"
        aria-pressed="false"
        @click.native="startSelecting"
      />
      <BaseRoundButton
        v-if="isPhone"
        :size="44"
        icon="more-horizontal"
        is-transparent
        :aria-label="$t('actions.more-actions')"
        @click.native="categorySheetOpen = true"
      />
      <div v-else class="profile-field-section__buttons" @click.stop>
        <BaseButton
          v-if="hasSelection"
          is-small
          is-danger
          has-icon-left
          class="profile-field-section__delete-selected"
          @click.native="onDeleteSelected"
        >
          <BaseIcon name="trash-2" />
          {{ $t('page.admin-tenant-profile.table.delete-entries') }}
        </BaseButton>
        <BaseRoundButton
          :size="32"
          :tooltip-text="$t('actions.delete')"
          icon="trash-2"
          is-red
          is-white
          @click.native="$emit('category-delete', category)"
        />
        <BaseRoundButton
          :size="32"
          :tooltip-text="$t('actions.edit')"
          icon="edit-2"
          is-white
          @click.native="$emit('category-edit', category)"
        />
      </div>
      <BaseButton
        v-if="!isPhone"
        :size="32"
        :tooltip-text="$t('page.admin-tenant-profile.add-entry')"
        icon="plus"
        is-white
        is-small
        has-icon-left
        @click.native="onAddEntry"
      >
        <BaseIcon name="plus" />
        {{ $t('page.admin-tenant-profile.add-entry') }}
      </BaseButton>
    </div>

    <div class="profile-field-section__body">
      <ul v-if="isPhone && entries.length" class="profile-field-section__lines">
        <BasePhoneRow
          v-for="entry in entries"
          :key="entry.id"
          opens-dialog
          :selectable="selecting"
          :selected="isSelected(entry)"
          @open="$emit('edit-entry', entry)"
          @toggle-select="toggle(entry)"
        >
          <template #title>
            <span class="base-phone-row__title">{{ entry.title }}</span>
          </template>
          <p class="base-phone-row__line profile-field-section__line-value">{{ localizedValue(entry) || '—' }}</p>
          <div v-if="entry.source === 'ai'" class="base-phone-row__meta">
            <BaseTag tone="ai" preset="soft" icon="ai">{{ $t('page.admin-tenant-profile.source-ai') }}</BaseTag>
          </div>
          <template v-if="!selecting" #actions>
            <button
              type="button"
              class="base-phone-row__action"
              :aria-label="$t('actions.more-actions')"
              aria-haspopup="dialog"
              @click.stop="sheetEntry = entry"
            >
              <BaseIcon name="more-horizontal" aria-hidden="true" />
            </button>
          </template>
        </BasePhoneRow>
      </ul>
      <TableNormal
        v-else-if="entries.length"
        v-model="selected"
        :headers="headers"
        :items="entries"
        :items-per-page="-1"
        :single-select="false"
        hide-default-footer
        item-key="id"
        show-select
        class="profile-field-section__table"
      >
        <template v-slot:header.data-table-select>
          <FormCheckbox
            :indeterminate="partiallySelected"
            :afterIndeterminate="false"
            :onChange="onSelectAllClick"
            :value="allSelected"
            :aria-label="$t('page.admin-tenant-profile.table.select-all')"
          />
        </template>
        <template v-slot:item.data-table-select="{ item, isSelected, select }">
          <FormCheckbox
            :value="isSelected"
            :aria-label="$t('page.admin-tenant-profile.table.select-row', { title: item.title })"
            @input="select($event)"
            @click.native.stop
          />
        </template>
        <template v-slot:item.title="{ item }">
          <div class="profile-field-section__label-row">
            <span class="profile-field-section__label">{{ item.title }}</span>
            <BaseTag v-if="item.source === 'ai'" tone="ai" preset="soft" icon="ai">
              {{ $t('page.admin-tenant-profile.source-ai') }}
            </BaseTag>
          </div>
        </template>
        <template v-slot:item.value="{ item }">
          <div class="profile-field-section__value-cell">
            <span class="profile-field-section__value" :title="localizedValue(item)">{{ localizedValue(item) }}</span>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="profile-field-section__row-actions">
            <BaseRoundButton
              :size="32"
              :tooltip-text="$t('actions.edit')"
              icon="edit-2"
              is-white
              @click.native.stop="$emit('edit-entry', item)"
            />
          </div>
        </template>
      </TableNormal>

      <div v-else class="profile-field-section__empty">
        <BaseButton isSmall has-icon-left @click.native="onAddEntry">
          <BaseIcon name="plus" />
          {{ $t('page.admin-tenant-profile.add-entry') }}
        </BaseButton>
      </div>
    </div>

    <BaseBottomSheet v-model="categorySheetOpen" :title="category.name">
      <ul class="base-bottom-sheet__menu">
        <li>
          <button type="button" @click="fromSheet(onAddEntry)">
            <BaseIcon name="plus" />
            {{ $t('page.admin-tenant-profile.add-entry') }}
          </button>
        </li>
        <li>
          <button type="button" @click="fromSheet(() => $emit('category-edit', category))">
            <BaseIcon name="edit-2" />
            {{ $t('actions.edit') }}
          </button>
        </li>
        <li>
          <button type="button" class="is-danger" @click="fromSheet(() => $emit('category-delete', category))">
            <BaseIcon name="trash-2" />
            {{ $t('actions.delete') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
    <BaseBottomSheet v-model="entrySheetOpen" :title="sheetEntry ? sheetEntry.title : ''">
      <ul v-if="sheetEntry" class="base-bottom-sheet__menu">
        <li>
          <button type="button" @click="fromSheet(() => $emit('edit-entry', sheetEntry))">
            <BaseIcon name="edit-2" />
            {{ $t('actions.edit') }}
          </button>
        </li>
        <li>
          <button
            type="button"
            class="is-danger"
            @click="fromSheet(() => $emit('delete-entries', { categoryId: category.id, ids: [sheetEntry.id] }))"
          >
            <BaseIcon name="trash-2" />
            {{ $t('actions.delete') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>
  </div>
</template>

<script>
import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BasePhoneRow from '@/components/base/BasePhoneRow.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import BaseTag from '@/components/base/BaseTag.vue';
import FormCheckbox from '@/components/form/FormCheckbox.vue';
import TableNormal from '@/components/table/TableNormal.vue';
import { fieldDisplayValue, localeToLang } from '@/util/tenant-profile';
import { viewport } from '@/util/viewport';

export default {
  name: 'ProfileFieldCategorySection',
  components: {
    BaseBottomSheet,
    BaseButton,
    BaseIcon,
    BasePhoneRow,
    BaseRoundButton,
    BaseTag,
    FormCheckbox,
    TableNormal,
  },
  props: {
    category: {
      type: Object,
      required: true,
    },
    entries: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selected: [],
      categorySheetOpen: false,
      // Phones: picking fields to delete several at once (larger screens tick the table's boxes).
      selecting: false,
      // The field whose sheet is open (phones).
      sheetEntry: null,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    selectingOnPhone() {
      return this.isPhone && this.selecting;
    },
    entrySheetOpen: {
      get() {
        return !!this.sheetEntry;
      },
      set(open) {
        if (!open) this.sheetEntry = null;
      },
    },
    currentLang() {
      return localeToLang(this.$i18n.locale);
    },
    headers() {
      return [
        {
          text: this.$t('page.admin-tenant-profile.table.column-title'),
          align: 'left',
          value: 'title',
          class: 'fixed-size-l',
        },
        { text: this.$t('page.admin-tenant-profile.table.column-value'), align: 'left', value: 'value' },
        {
          text: '',
          align: 'right',
          sortable: false,
          value: 'actions',
          class: 'fixed-size-action',
          cellClass: 'fixed-size-action',
        },
      ];
    },
    hasSelection() {
      return this.selected.length > 0;
    },
    allSelected() {
      return this.entries.length > 0 && this.selected.length === this.entries.length;
    },
    partiallySelected() {
      return this.hasSelection && !this.allSelected;
    },
  },
  watch: {
    // Drop selections that are no longer visible (e.g. after search/filter changes).
    entries() {
      const ids = new Set(this.entries.map((e) => e.id));
      this.selected = this.selected.filter((e) => ids.has(e.id));
    },
  },
  methods: {
    // As in every sheet, a choice closes it first.
    fromSheet(action) {
      this.categorySheetOpen = false;
      action();
      this.sheetEntry = null;
    },
    onAddEntry() {
      this.$emit('add-entry', this.category);
    },
    onDeleteSelected() {
      this.$emit('delete-entries', { categoryId: this.category.id, ids: this.selected.map((e) => e.id) });
      this.selected = [];
      this.selecting = false;
    },
    startSelecting() {
      this.selecting = true;
    },
    stopSelecting() {
      this.selecting = false;
      this.selected = [];
    },
    isSelected(entry) {
      return this.selected.some((e) => e.id === entry.id);
    },
    toggle(entry) {
      this.selected = this.isSelected(entry)
        ? this.selected.filter((e) => e.id !== entry.id)
        : [...this.selected, entry];
    },
    onSelectAllClick() {
      this.selected = this.allSelected || this.partiallySelected ? [] : [...this.entries];
    },
    // Show the current locale's value, falling back to the field's primary value.
    localizedValue(item) {
      return fieldDisplayValue(item, this.currentLang);
    },
  },
};
</script>

<style lang="scss">
.profile-field-section {
  background: $color-surface-raised;
  border: 1px solid $color-border;
  border-radius: $radius-md;

  &__header {
    display: flex;
    align-items: center;
    gap: $space-sm;
    padding: $space-3 $space-md;
    // Sticky so the category name stays visible while scrolling its fields.
    position: sticky;
    top: 0;
    z-index: 2;
    background: $color-surface-raised;
    border-radius: $radius-md $radius-md 0 0;
  }

  &__name {
    @include text-style('heading-sm');
    font-family: $font-family-heading;
    color: $color-text-title;
    margin: 0;
    // Keep category names on a single line.
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  &__header.is-selecting {
    gap: $space-2;
    padding-left: $space-3;
  }

  &__selected {
    flex: 1;
    min-width: 0;
    @include text-style('body-sm');
    font-weight: $font-weight-medium;
    color: $color-text-title;
  }

  &__count {
    flex: none;
    color: $color-text-muted;
    @include text-style('body-sm');
  }

  &__spacer {
    flex: 1;
  }

  &__buttons {
    display: flex;
    align-items: center;
    gap: $space-1;
    flex-shrink: 0;
  }

  &__delete-selected {
    margin-right: $space-sm;
  }

  &__body {
    border-top: 1px solid $color-border;
    padding: 0 $space-md $space-sm;
  }

  &__label-row {
    display: flex;
    align-items: center;
    gap: $space-sm;
    padding: $space-2 0;
  }

  &__label {
    @include text-style('body-md');
    color: $color-text-title;
    font-weight: $font-weight-medium;
  }

  &__value-cell {
    display: flex;
    align-items: center;
    gap: $space-sm;
    min-width: 0;
  }

  &__value {
    @include text-style('body-md');
    color: $color-text-body;
    display: block;
    max-width: 42ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__row-actions {
    display: flex;
    justify-content: flex-end;
  }

  &__lines {
    margin: 0;
    padding: 0;

    > .base-phone-row:last-child {
      border-bottom: 0;
    }
  }

  &__line-value {
    overflow-wrap: anywhere;
  }

  // Phones: the lines run to the box's edges, and the header leaves room for its round button.
  @include screen-below('tablet') {
    &__header {
      padding: $space-1 $space-1 $space-1 $space-md;
    }

    &__body {
      padding: 0;
    }
  }

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 96px;
  }

  @media (prefers-reduced-motion: reduce) {
    .profile-field-section__header {
      transition-duration: 0ms;
    }
  }
}

.profile-field-section__table.v-data-table {
  background: transparent;

  th.fixed-size-action,
  td.fixed-size-action {
    width: 64px;
    white-space: nowrap;
    text-align: right;
  }

  .v-data-table-header th {
    &.fixed-size-l {
      width: 30%;
    }
  }

  tbody tr {
    td {
      border-bottom: 1px solid $color-border !important;
      vertical-align: middle;
      height: 40px !important;
    }

    &:last-child td {
      border-bottom: none !important;
    }

    &:hover {
      background: $color-surface-sunken;
    }
  }
}
</style>
